/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1777420800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Wed Apr 29 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "ometria",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to Ometria",
          "depth": 0,
          "id": "connect-to-ometria"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Syncing contacts, orders, and products",
          "depth": 1,
          "id": "syncing-contacts-orders-and-products"
        }, {
          "value": "Syncing events",
          "depth": 1,
          "id": "syncing-events"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      p: "p",
      ul: "ul",
      li: "li",
      h3: "h3",
      h4: "h4"
    }, _provideComponents(), props.components), {EventSyncWarning, NoCommonErrors, LiveDebugger, SyncAlerts} = _components;
    if (!EventSyncWarning) _missingMdxReference("EventSyncWarning", true);
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            }), _jsx(_components.th, {
              children: "API Reference"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Contacts"
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to contacts in Ometria"
            }), _jsx(_components.td, {
              children: "Upsert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://docs.ometria.com/docs/contacts-endpoint",
                children: "Contacts docs"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Orders"
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to orders in Ometria"
            }), _jsx(_components.td, {
              children: "Upsert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://docs.ometria.com/docs/orders-endpoint",
                children: "Orders docs"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Products"
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to products in Ometria"
            }), _jsx(_components.td, {
              children: "Upsert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://docs.ometria.com/docs/products-product-listings-and-product-variants#products",
                children: "Products docs"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Events"
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to events in Ometria"
            }), _jsx(_components.td, {
              children: "Insert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://support.ometria.com/hc/en-gb/articles/360011171077-Setting-up-custom-events",
                children: "Events docs"
              })
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["All sync types use the ", _jsx(_components.a, {
          href: "https://docs.ometria.com/reference/post_push",
          children: "push endpoint"
        }), " to sync data to Ometria."]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-ometria",
        children: "Connect to Ometria"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations?from=docs",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add destination"
        }), " button. Select ", _jsx(_components.strong, {
          children: "Ometria"
        }), " and click ", _jsx(_components.strong, {
          children: "Continue"
        }), ". You can then authenticate Hightouch to ", _jsx(_components.strong, {
          children: "Ometria"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "Enter the following fields into Hightouch:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "API key:"
          }), " To create a new API key, refer to Ometria's ", _jsx(_components.a, {
            href: "https://support.ometria.com/hc/en-gb/articles/9882706389917-Creating-a-new-API-key",
            children: "API keys docs"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've set up your Ometria destination and have a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " to pull data from, you can set up your sync configuration to begin syncing data. Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs?from=docs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add sync"
        }), " button to begin. Then, select the relevant model and the Ometria destination you want to sync to."]
      }), "\n", _jsx(_components.h3, {
        id: "syncing-contacts-orders-and-products",
        children: "Syncing contacts, orders, and products"
      }), "\n", _jsx(_components.p, {
        children: "Sync data from any source to contacts, orders, and products in Ometria."
      }), "\n", _jsx(_components.h4, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsxs(_components.p, {
        children: ["To match rows from your model to objects in Ometria, you need to select the model column that contains values that match the ", _jsx(_components.strong, {
          children: "ID"
        }), " field.\nRefer to the ", _jsx(_components.a, {
          href: "/syncs/record-matching",
          children: "record matching docs"
        }), " for more information."]
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch lets you sync contact fields via ", _jsx(_components.a, {
          href: "/syncs/mapping-data",
          children: "field mapping"
        }), ".\nYou can map data from any of your model columns to default and custom contact fields."]
      }), "\n", _jsx(_components.p, {
        children: "For orders, Ometria requires the following fields, so you must map them to complete your configuration:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Timestamp"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Grand total"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Subtotal"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Currency"
          })
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "For products, Ometria requires the following fields, so you must map them to complete your configuration:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Title"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Price"
          })
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Ensure your model's columns have the same data types as the fields you want to sync to."
      }), "\n", _jsx(_components.h3, {
        id: "syncing-events",
        children: "Syncing events"
      }), "\n", _jsx(_components.p, {
        children: "Sync data from any source to events in Ometria."
      }), "\n", _jsx(EventSyncWarning, {}), "\n", _jsx(_components.h4, {
        id: "record-matching-1",
        children: "Record matching"
      }), "\n", _jsxs(_components.p, {
        children: ["You can match rows from your model to events in Ometria on any column in your model and any field in Ometria. Ensure the data types of the model column and Ometria field you select match.\nRefer to the ", _jsx(_components.a, {
          href: "/syncs/record-matching",
          children: "record matching docs"
        }), " for more information."]
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping-1",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch lets you sync event properties via field mapping."
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
